import { ErrorBoundary } from "@sentry/react";
import { AgGridReact } from "ag-grid-react";
import { Button, message, Progress } from "antd";
import {
  ECustomUserTypes,
  GanttActivityTypes,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { ScheduleTaskType } from "pages/submittal-schedule-linking/models";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DateFilter, DateUtils } from "utils/dateutils";
import {
  dummyColumnDef,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { QUERY_GET_SCHEDULE_VERSIONS } from "services/graphQL/queries";
import {
  GridOptions,
  GetContextMenuItemsParams,
  ColDef,
  ValueFormatterParams,
  MenuItemDef,
  DefaultMenuItem
} from "ag-grid-enterprise";
import ExpandIconPath from "assets/svg/expand-icon.svg";
import CollapseIconPath from "assets/svg/collapse-icon.svg";
import { GridLoadingIndicator } from "components/widgets";
import { getFileFromS3 } from "services/schedule-versions-services";
import { useHistory, useParams } from "react-router";
import {
  getScheduleFileExtension,
  matchCustomUserProjectRole
} from "utils/utils";
import "../project-schedule-details.scss";
import CiqLogPageHeader from "components/ciq-log-page-header";
import CiqAgSearch from "components/ciq-ag-search";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import { FEATURE_ACTIVITY_LIST } from "entity-app/graphQL/ciq-feature-queries";

function FeatureScheduleDetailspage() {
  const { projectId, selectedVersionId }: any = useParams();

  const [selectedfile, setSelectedFile] = useState<any>();
  const { gqlClientForProject, tokenContents } = useContext(ProjectContext);
  const gridRef = useRef<AgGridReact<ScheduleTaskType>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [treeRowData, setTreeRowData] = useState<Array<ScheduleTaskType>>();
  const history = useHistory();

  const { data: scheduleListData } = useCIQQuery(QUERY_GET_SCHEDULE_VERSIONS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  useEffect(() => {
    if (selectedVersionId && scheduleListData?.schedule_import_details) {
      const foundVersion = scheduleListData?.schedule_import_details.find(
        (ver: any) => ver.import_log_id === selectedVersionId
      );

      setSelectedFile(foundVersion);
    } else {
      setSelectedFile(null);
    }
  }, [scheduleListData?.schedule_import_details, selectedVersionId]);

  const isGCUser = matchCustomUserProjectRole(
    ECustomUserTypes.AllGCUserRole,
    tokenContents?.role
  );

  const { data: tasksData, loading: taskLoading } = useCIQQuery(
    FEATURE_ACTIVITY_LIST,
    {
      client: gqlClientForProject,
      fetchPolicy: "no-cache",
      skip: !gqlClientForProject
    }
  );

  function setNodesCollapseandExpand(id: string, isExpanded: boolean) {
    gridRef.current?.api.forEachNode((node) => {
      if (node.data?.source_task_id === id) {
        // const isExpanded = !node.expanded;
        gridRef.current?.api.setRowNodeExpanded(node, isExpanded);
        // eslint-disable-next-line array-callback-return
        node?.allLeafChildren?.map((leafNode) => {
          gridRef.current?.api.setRowNodeExpanded(
            leafNode,
            leafNode.expanded === isExpanded
              ? leafNode.expanded
              : !leafNode.expanded
          );
        });
      }
    });
  }

  function criticalActivityCellRenderer(params: any) {
    if (params.data.critical && params.data.type !== GanttActivityTypes.Wps) {
      return (
        <div
          className="flex items-center justify-center h-full"
          data-testid="critical-activity-tag-container"
        >
          {CriticalActivityTag()}
        </div>
      );
    }
    return "";
  }

  const isCSVFile = useMemo(() => {
    return getScheduleFileExtension(selectedfile, "csv");
  }, [selectedfile]);

  const progressCellRenderer = ({ data }: any) => {
    const progress = Math.floor(data?.progress || 0);
    return (
      <Progress
        percent={progress}
        steps={4}
        strokeColor="#9E9E9E"
        format={(percent) => {
          return `${percent}%`;
        }}
        data-testid="activity-progress"
      />
    );
  };

  const columnDefs: ColDef[] = useMemo<ColDef[]>(() => {
    const colArr: any = [
      dummyColumnDef,
      {
        colId: "source_task_id",
        field: "source_task_id",
        headerName: "Activity ID",
        headerTooltip: "Activity ID",
        tooltipField: "source_task_id",
        showRowGroup: true,
        filter: true,
        minWidth: 150,
        cellRenderer: "agGroupCellRenderer",
        sort: "desc"
      },
      {
        colId: "text",
        field: "text",
        headerName: "Activity Name",
        headerTooltip: "Activity Name",
        minWidth: 150,
        filter: true,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "total_slack",
        field: "total_slack",
        headerName: "Activity Float",
        headerTooltip: "Activity Float",
        cellStyle: (params: any) => {
          if (params?.data?.critical) {
            return { fontWeight: "bold" };
          }
          return { fontWeight: "normal" };
        },
        valueGetter: (params: any) => {
          if (
            params?.data?.total_slack === null ||
            params.data.type === GanttActivityTypes.Wps
          )
            return "";
          const float = Math.floor((params?.data?.total_slack || 0) / 8);
          return `${float} days`;
        },
        cellRenderer: truncatedTextCellRenderer,
        getQuickFilterText: () => ""
      },
      {
        colId: "critical",
        field: "critical",
        headerName: "Critical Activity",
        headerTooltip: "Critical Activity",
        cellRenderer: criticalActivityCellRenderer,
        minWidth: 170,
        maxWidth: 170,
        filter: "agSetColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          valueFormatter: (params: ValueFormatterParams) => {
            return params?.value === true ? "Critical" : "Non Critical";
          },
          comparator: (valA: any, valB: any) => {
            if (valA === valB) return 0;
            return valA > valB ? -1 : 1;
          }
        },
        getQuickFilterText: () => ""
      },
      {
        colId: "start_date",
        field: "$raw.Start",
        headerName: "Planned Start Date",
        headerTooltip: "Planned Start Date",
        valueGetter: ({ data }: any) => {
          if (selectedfile?.is_active) {
            return data?.start_date
              ? DateUtils.format(data?.start_date, "MM-DD-YYYY")
              : "";
          }
          return data?.$raw?.Start
            ? DateUtils.format(data?.$raw?.Start, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 120,
        maxWidth: 170,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "end_date",
        field: "$raw.Finish",
        headerName: "Planned End Date",
        headerTooltip: "Planned End Date",
        valueGetter: ({ data }: any) => {
          if (selectedfile?.is_active) {
            return data?.end_date
              ? DateUtils.format(data?.end_date, "MM-DD-YYYY")
              : "";
          }
          return data?.$raw?.Finish
            ? DateUtils.format(data?.$raw?.Finish, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 120,
        maxWidth: 170,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "duration",
        field: "duration",
        headerName: "Duration",
        headerTooltip: "Duration",
        minWidth: 100,
        maxWidth: 100,
        filter: true,
        valueGetter: (params: any) => {
          const duration = Math.floor((params?.data?.duration || 0) / 8);
          return duration;
        },

        getQuickFilterText: () => ""
      },
      {
        colId: "actual_start_date",
        field: "actual_start_date",
        headerName: "Actual Start Date",
        headerTooltip: "Actual Start Date",
        valueGetter: ({ data }: any) => {
          return data?.start_date
            ? DateUtils.format(data?.actual_start_date, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 160,
        maxWidth: 160,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "actual_finish_date",
        field: "actual_finish_date",
        headerName: "Actual End Date",
        headerTooltip: "Actual End Date",
        valueGetter: ({ data }: any) => {
          return data?.actual_finish_date
            ? DateUtils.format(data?.actual_finish_date, "MM-DD-YYYY")
            : "";
        },
        comparator: DateFilter.comparator,
        minWidth: 150,
        maxWidth: 150,
        filter: false,
        getQuickFilterText: () => ""
      },
      {
        colId: "progress",
        headerName: "Progress",
        headerTooltip: "Progress",
        cellRenderer: progressCellRenderer
      }
    ];

    if (!isGCUser || isCSVFile) {
      const csvFileSkippedColumns = [
        "total_slack",
        "critical",
        "actual_start_date",
        "actual_finish_date",
        "progress"
      ];
      return colArr?.filter(
        (col: ColDef) => !csvFileSkippedColumns.includes(col?.colId ?? "")
      );
    }
    return colArr;
  }, [isCSVFile, isGCUser, selectedfile?.is_active]);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const result: MenuItemDef[] = [
        {
          // custom item
          name: "Expand All",
          action: () => {
            setNodesCollapseandExpand(
              params?.node?.data?.source_task_id || "",
              true
            );
          },
          icon: `<img src='${ExpandIconPath}'>`
        },
        {
          // custom item
          name: "Collapse All",
          action: () => {
            setNodesCollapseandExpand(
              params?.node?.data?.source_task_id || "",
              false
            );
          },
          icon: `<img src='${CollapseIconPath}'>`
        }
      ];

      return (params?.node?.allLeafChildren || []).length > 1 ? result : [];
    },
    []
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      filter: false,
      sortable: false,
      resizable: true,
      editable: false,
      menuTabs: [],
      getContextMenuItems,
      cellRendererParams: {
        suppressCount: true
      }
    };
  }, [getContextMenuItems]);

  const gridOptions: GridOptions = {
    defaultColDef,
    suppressCopyRowsToClipboard: true,
    getContextMenuItems
  };

  useEffect(() => {
    if (!selectedfile) return;
    const filepath = selectedfile?.import_log?.resolved_values_s3_prefix;
    if (selectedfile?.is_active) {
      if (tasksData) {
        const filteredTasks = tasksData?.getScheduleDataByProject?.filter(
          (ts: ScheduleTaskType) => ts.type !== 3 && ts.type !== 4
        );

        const addParent = (t: ScheduleTaskType, org: Array<any>) => {
          if (t.parent) {
            const parent = filteredTasks?.find(
              (ts: ScheduleTaskType) => ts.id === t.parent
            );
            if (parent) addParent(parent, org);
          }
          org.push(t.id);
        };
        const tree = filteredTasks?.map((t: ScheduleTaskType) => {
          const org: any[] = [];
          if (t) addParent(t, org);
          return { org, ...t };
        });
        setTreeRowData(tree);
        gridRef?.current?.api?.hideOverlay();
      } else if (!taskLoading) {
        gridRef?.current?.api?.hideOverlay();
        gridRef?.current?.api?.showNoRowsOverlay();
        setTreeRowData([]);
      }
    } else if (filepath) {
      getFileFromS3(filepath).then((responseData) => {
        if (responseData?.error) {
          gridRef?.current?.api?.hideOverlay();
          gridRef?.current?.api?.showNoRowsOverlay();
          setTreeRowData([]);
          message.error("Unable to view selected schedule, please try again.");
          return;
        }
        if (responseData) {
          const addParent = (t: ScheduleTaskType, org: Array<any>) => {
            const tempTsk = { ...t };
            const taskInfo: any = tempTsk.$custom_data;
            tempTsk.critical = taskInfo?.Critical === "1";
            tempTsk.total_slack = taskInfo?.TotalSlack;

            if (tempTsk?.parent) {
              const parent = responseData?.data?.data?.find(
                (ts: ScheduleTaskType) => ts.id === tempTsk.parent
              );
              if (parent) addParent(parent, org);
            }
            if (t) {
              org.push(tempTsk?.id);
            }
          };
          const tree = responseData?.data?.data?.map((t: ScheduleTaskType) => {
            const org: any[] = [];
            const tempTsk = { ...t };
            const taskInfo: any = tempTsk.$custom_data;
            tempTsk.critical = taskInfo?.Critical === "1";
            tempTsk.total_slack = taskInfo?.TotalSlack;
            if (tempTsk) addParent(tempTsk, org);
            return { org, ...tempTsk };
          });
          setTreeRowData(tree);
          gridRef?.current?.api?.hideOverlay();
        }
      });
    } else {
      gridRef?.current?.api?.hideOverlay();
      gridRef?.current?.api?.showNoRowsOverlay();
    }
  }, [tasksData, selectedfile, taskLoading]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const getDataPath = useMemo(() => {
    return (data: any) => {
      return data.org;
    };
  }, []);

  const headerItems = useMemo(() => {
    const items = [
      <CiqAgSearch
        key="Schedules_search"
        gridRef={gridRef}
        placeholder="Search Schedules"
        data-testid="schedule-search"
      />
    ];
    if (!selectedfile?.is_active) {
      items.push(
        <Button
          type="primary"
          hidden={isPermissionNotGrantted(
            ProjectPermissionEnum.ImportSchedule,
            tokenContents?.role!
          )}
          onClick={() => {
            history.push(
              `/project/${projectId}/schedule/change-impact?&selectedVersionId=${selectedfile.import_log.id}`
            );
          }}
          data-testid="compare-with-active-button"
        >
          Compare with active version
        </Button>
      );
    }
    return items;
  }, [
    history,
    projectId,
    selectedfile?.import_log.id,
    selectedfile?.is_active,
    tokenContents?.role
  ]);

  return (
    <div className="flex flex-col w-full h-full">
      <CiqLogPageHeader
        entityName={selectedfile?.schedule_name || ""}
        items={headerItems}
        backButton={() => {
          history.replace(`/project/${projectId}/schedule/project-schedules`);
        }}
        data-testid="schedule-details-header"
      />

      <ErrorBoundary>
        <div className="flex flex-col h-full w-full ag-theme-alpine">
          <AgGridReact<ScheduleTaskType>
            ref={gridRef}
            rowData={treeRowData}
            columnDefs={columnDefs}
            gridOptions={gridOptions}
            defaultColDef={defaultColDef}
            animateRows={false}
            onGridReady={() => {
              setGridReady(true);
              gridRef.current?.api.sizeColumnsToFit();
            }}
            loadingOverlayComponent={GridLoadingIndicator}
            rowSelection="single"
            treeData
            groupDefaultExpanded={-1}
            getDataPath={getDataPath}
            groupDisplayType="custom"
            suppressCellFocus
            suppressDragLeaveHidesColumns
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            tooltipShowMode="whenTruncated"
            excludeChildrenWhenTreeDataFiltering
          />
        </div>
      </ErrorBoundary>
    </div>
  );
}

export default FeatureScheduleDetailspage;
